import * as React from "react";
import { Shield } from "lucide-react";
import type {
  GameSheetBoxScore,
  GameSheetMatchup,
  GameSheetMatchupTeam,
  GameSheetTeam,
  GameSheetTeamStats,
} from "./useGameSheetBoxScore";

/**
 * What shows below the player when a game has no condensed game: the
 * GameSheet box score's season-to-date summary instead of an empty strip.
 *
 * Follows the approved mockup: team blocks on the outer edges, a divider-row
 * comparison table in the center. It carries NO HEADING of its own. The
 * mockup wants a thin strip under the player, and a title line on a panel
 * that already sits directly beneath the video only states where you are.
 *
 * There is no FINAL row either. The score is on the player and on the page
 * around it; repeating it here cost a row on the one axis this strip is
 * trying to save.
 *
 * Padding and gaps are inline for the same reason as CondensedGameClips:
 * Tailwind padding utilities resolve to 0 inside `.v2` because of the global
 * Bootstrap reset (see src/v2/styles/v2.css).
 *
 * The row (team / stat table / team) needs roughly 830px of unbroken width
 * to lay its fixed-width pieces (two 64px crests, a 260px stat column, the
 * gaps) out side by side — well past Tailwind's 640px `sm:` breakpoint, so a
 * tablet in portrait (768–834px) still has the full-size row and overflows
 * past the card edge. It stacks below 900px instead. That needs a real media
 * query, which an inline style can't express, so the row, the stat column
 * and each team block carry `v2-game-details-*` classes whose responsive
 * behavior lives in v2.css; the crest size uses the matching `min-[900px]:`
 * variant so it switches at the same breakpoint instead of Tailwind's `sm:`.
 * The row also keeps `flex-wrap` as a fallback so an unusually long team
 * name still wraps instead of clipping, even above 900px.
 */

/**
 * What a missing number looks like. An en dash, because the alternative was
 * the string "undefined", which is what this panel used to print.
 */
const NONE = "\u2013";

function has(v: unknown): boolean {
  return v !== null && v !== undefined && v !== "";
}

/**
 * Wins-losses-OT/shootout-losses-ties, the season record a fan reads a team by.
 *
 * NULL-SAFE ON PURPOSE. `boxscore.<side>.record` is an EMPTY OBJECT until a
 * game is final, so every field here is undefined before puck drop, and the
 * template literal that used to build this line rendered
 * "undefined-undefined-undefined-undefined" on every scheduled game. Returns
 * null instead, so a caller can fall back to the season form in `matchup`.
 */
function recordLine(record: GameSheetTeam["record"]): string | null {
  const { wins, losses, overtimeShootoutLosses, ties } = record ?? {};
  if (![wins, losses, overtimeShootoutLosses, ties].every(has)) return null;
  return `${wins}-${losses}-${overtimeShootoutLosses}-${ties}`;
}

/**
 * Whether a record string upstream sent carries information.
 *
 * GameSheet returns "0-0-0-0" for every seasonStandings field, and for
 * last5Overall, on an exhibition schedule. That is a placeholder for "there are
 * no standings", not a team that has lost nothing and won nothing, and reading
 * it as a record is how the RECORD row came to disagree with the record printed
 * under the crest beside it.
 */
function isZeroed(value: string | null | undefined): boolean {
  return typeof value === "string" && /^0(\s*-\s*0)+$/.test(value.trim());
}

/**
 * A W-L-OTL-T line counted from the games themselves.
 *
 * last5GameLog survives exhibition play with real results in it, so it is what
 * LAST 5 GAMES falls back to. Newest first, hence the plain slice.
 *
 * An unrecognised result code withholds the whole line rather than skipping the
 * entry: a team's last five reported as four games is a quieter kind of wrong
 * than no line at all, and therefore a worse one.
 */
const RESULT_BUCKET: Record<string, number> = {
  W: 0, WIN: 0,
  L: 1, LOSS: 1,
  OTL: 2, SOL: 2, OL: 2, SL: 2,
  T: 3, TIE: 3,
};

function lastFiveFromLog(
  log: GameSheetTeamStats["last5GameLog"] | undefined,
): string | null {
  if (!Array.isArray(log) || log.length === 0) return null;
  const tally = [0, 0, 0, 0];
  for (const entry of log.slice(0, 5)) {
    const bucket = RESULT_BUCKET[String(entry?.resultCode ?? "").toUpperCase()];
    if (bucket === undefined) return null;
    tally[bucket] += 1;
  }
  return tally.join("-");
}

/**
 * The record to show, preferring the season standing over this game's.
 *
 * A fan reading "RECORD" means the team's season, which is what
 * `matchup.stats.seasonStandings.thisSeasonStats` holds and what survives
 * before a game is played. The box score's own record is the fallback, and it
 * is the one that exists only afterwards.
 *
 * A ZEROED standing yields to the box score, because in exhibition play the
 * standing is the placeholder "0-0-0-0" while the box score holds the real
 * record. It is only preferred when the box score has something to say: a team
 * that genuinely has not played IS 0-0-0-0, and replacing that with a dash
 * would trade one wrong answer for another.
 */
function seasonRecord(
  stats: GameSheetTeamStats | null | undefined,
  record: GameSheetTeam["record"],
): string {
  const standing = stats?.seasonStandings?.thisSeasonStats ?? null;
  const fromBoxScore = recordLine(record);
  if (isZeroed(standing) && fromBoxScore) return fromBoxScore;
  return standing ?? fromBoxScore ?? NONE;
}

function TeamCrest({ logo }: { logo: string | null }) {
  if (logo) {
    return (
      <img
        src={logo}
        alt=""
        className="w-16 h-16 shrink-0 object-contain"
      />
    );
  }
  return (
    <Shield
      className="size-12 shrink-0 text-slate-300"
      aria-hidden="true"
    />
  );
}

/**
 * City and mascot as the tenant's own player API already splits them
 * (`away_team`/`away_team_mascot`, `home_team`/`home_team_mascot`) — real
 * structured fields, not a guess at where GameSheet's single `title` string
 * (e.g. "Corpus Christi Icerays") splits. Falls back to GameSheet's title
 * on one line if the tenant data is missing either half.
 */
export type TeamNameInfo = { city: string; mascot: string };

function TeamSide({
  team,
  name,
  align,
}: {
  team: GameSheetTeam;
  name: TeamNameInfo;
  align: "left" | "right";
}) {
  const hasSplitName = Boolean(name.city && name.mascot);

  return (
    <div
      className={
        "v2-game-details-team flex flex-1 items-center " +
        (align === "left" ? "justify-start" : "justify-end")
      }
      style={{ gap: "12px" }}
    >
      <TeamCrest logo={team.logo} />
      <div
        className="v2-game-details-team-info flex flex-col items-start text-left"
        style={{ gap: "1px" }}
      >
        {hasSplitName ? (
          <>
            <div className="text-sm font-bold uppercase" style={{ color: "var(--v2-primary)" }}>
              {name.city}
            </div>
            <div className="text-lg font-extrabold uppercase text-slate-900" style={{ lineHeight: 1.1 }}>
              {name.mascot}
            </div>
          </>
        ) : (
          <div className="text-lg font-extrabold uppercase text-slate-900" style={{ lineHeight: 1.1 }}>
            {name.city || name.mascot || team.title}
          </div>
        )}
        <div className="text-sm font-bold" style={{ color: "var(--v2-primary)", marginTop: "2px" }}>
          {recordLine(team.record)}
        </div>
        {team.division?.title && (
          <div className="text-xs text-slate-400">{team.division.title}</div>
        )}
      </div>
    </div>
  );
}

/** Goals for and against, the way a fan reads a scoring differential. */
function goalsLine(record: GameSheetTeam["record"]): string {
  const { goalsFor, goalsAgainst } = record ?? {};
  if (!has(goalsFor) || !has(goalsAgainst)) return NONE;
  return `${goalsFor}-${goalsAgainst}`;
}

/** Power-play goals over opportunities, e.g. "2/5". */
function powerPlayLine(powerPlays: GameSheetTeam["powerPlays"]): string {
  const { goals, opportunities } = powerPlays ?? {};
  if (!has(goals) || !has(opportunities)) return NONE;
  return `${goals}/${opportunities}`;
}

/**
 * "Won 1", "Lost 3", or a dash. Upstream already phrases it; nothing here
 * re-words it, because a streak written two ways in one product is worse than
 * a streak written upstream's way.
 */
function streakLine(stats: GameSheetTeamStats | null | undefined): string {
  return stats?.streak ?? NONE;
}

function lastFiveLine(stats: GameSheetTeamStats | null | undefined): string {
  const summary = stats?.last5Overall ?? null;
  // Counted from the games when the summary is the exhibition placeholder.
  if (isZeroed(summary)) {
    return lastFiveFromLog(stats?.last5GameLog) ?? summary ?? NONE;
  }
  return summary ?? NONE;
}

function lastSeasonLine(stats: GameSheetTeamStats | null | undefined): string {
  const line = stats?.seasonStandings?.lastSeasonStats ?? null;
  // Nothing in the box score covers a PREVIOUS season, so a zeroed value has no
  // better source to fall back to and becomes a dash. "Not recorded" should not
  // read as a confident 0-0-0-0.
  if (isZeroed(line)) return NONE;
  return line ?? NONE;
}

/**
 * Pairs each side of the matchup with the right box-score team BY ID.
 *
 * `teamA` is the home side in every response observed, and matching on that
 * rather than on position is the difference between a fan reading their own
 * team's form and reading their opponent's. The ids are right there; there is
 * no reason to trust an ordering instead.
 */
function statsFor(
  matchup: GameSheetMatchup,
  teamId: number,
): GameSheetTeamStats | null {
  const teams = matchup?.teams;
  if (!teams) return null;
  const sides: Array<GameSheetMatchupTeam | undefined> = [teams.teamA, teams.teamB];
  return sides.find((t) => t?.id === teamId)?.stats ?? null;
}

/**
 * "MARYLAND LEADS 6-0", or null when neither side has won one.
 *
 * Built from the head-to-head record rather than counted from a game list,
 * because upstream already keeps it and a second way of counting is a second
 * way of being wrong. A tie is stated as a tie rather than dropped: "0-0"
 * between two teams that have met is different from not having met.
 */
function seriesLine(
  matchup: GameSheetMatchup,
  visitorName: string,
  homeName: string,
): string | null {
  const h2h = matchup?.headToHead_thisSeason;
  if (!h2h) return null;
  // "W-L-OTL-T" per side; the wins are the first field.
  const homeWins = Number(String(h2h.teamA ?? "").split("-")[0]);
  const awayWins = Number(String(h2h.teamB ?? "").split("-")[0]);
  if (!Number.isFinite(homeWins) || !Number.isFinite(awayWins)) return null;
  if (homeWins === 0 && awayWins === 0) return null;
  if (homeWins === awayWins) return `SERIES TIED ${homeWins}-${awayWins}`;
  const leader = homeWins > awayWins ? homeName : visitorName;
  const hi = Math.max(homeWins, awayWins);
  const lo = Math.min(homeWins, awayWins);
  return `${leader.toUpperCase()} LEADS ${hi}-${lo}`;
}

function StatRow({
  left,
  label,
  right,
  isLast,
}: {
  left: React.ReactNode;
  label: string;
  right: React.ReactNode;
  isLast?: boolean;
}) {
  return (
    <div
      className="grid items-center text-center"
      style={{
        gridTemplateColumns: "1fr 1fr 1fr",
        padding: "5px 0",
        borderBottom: isLast ? undefined : "1px solid #e2e8f0",
      }}
    >
      <div className="text-sm font-bold" style={{ color: "var(--v2-primary)" }}>{left}</div>
      <div className="text-xs font-semibold uppercase text-slate-400" style={{ letterSpacing: "0.03em" }}>
        {label}
      </div>
      <div className="text-sm font-bold" style={{ color: "var(--v2-primary)" }}>{right}</div>
    </div>
  );
}

export function GameDetailsFallback({
  boxScore,
  matchup = null,
  isLive = false,
  awayTeamName,
  homeTeamName,
}: {
  boxScore: GameSheetBoxScore;
  matchup?: GameSheetMatchup;
  /**
   * The tenant's own `is_live` for this game: true before and during, false
   * once it is over. Preferred over the box score's `status` because it is what
   * the rest of the page already switches on, and one page deciding twice
   * whether a game has finished is one page that will eventually disagree with
   * itself.
   */
  isLive?: boolean;
  awayTeamName: TeamNameInfo;
  homeTeamName: TeamNameInfo;
}) {
  const { home, visitor, season, arena, attendance, threeStars } = boxScore;

  const visitorStats = statsFor(matchup, visitor.id);
  const homeStats = statsFor(matchup, home.id);

  /*
   * ONE LAYOUT, TWO SETS OF ROWS.
   *
   * Before a game there is no scoring to report, so the rows are the season
   * form a fan uses to decide whether to watch: record, last five, streak, and
   * how the two did last season. Afterwards those are replaced by what
   * happened, because a fan who has just watched a game wants the game.
   *
   * Both are built from the same StatRow, so the two states are the same
   * component with different rows rather than two designs that drift apart.
   */
  const rows: Array<{ left: string | number; label: string; right: string | number }> =
    isLive
      ? [
        { left: seasonRecord(visitorStats, visitor.record), label: "RECORD", right: seasonRecord(homeStats, home.record) },
        { left: lastFiveLine(visitorStats), label: "LAST 5 GAMES", right: lastFiveLine(homeStats) },
        { left: streakLine(visitorStats), label: "CURRENT STREAK", right: streakLine(homeStats) },
        { left: lastSeasonLine(visitorStats), label: "LAST SEASON", right: lastSeasonLine(homeStats) },
      ]
      : [
        { left: seasonRecord(visitorStats, visitor.record), label: "RECORD", right: seasonRecord(homeStats, home.record) },
        { left: goalsLine(visitor.record), label: "GOALS FOR-AGAINST", right: goalsLine(home.record) },
        { left: powerPlayLine(visitor.powerPlays), label: "POWER PLAY", right: powerPlayLine(home.powerPlays) },
        { left: streakLine(visitorStats), label: "CURRENT STREAK", right: streakLine(homeStats) },
      ];

  const series = seriesLine(
    matchup,
    awayTeamName.city || visitor.title,
    homeTeamName.city || home.title,
  );

  const metaLine = [
    season?.title,
    arena?.location,
    attendance != null ? `${attendance} in attendance` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const stars = [threeStars?.first, threeStars?.second, threeStars?.third].filter(
    (p): p is NonNullable<typeof p> => Boolean(p),
  );

  return (
    <div
      className="v2-game-details-card flex flex-col border border-slate-200 bg-white"
      style={{ padding: "12px 20px", gap: "8px" }}
    >
      <div className="v2-game-details-row flex items-center" style={{ gap: "24px" }}>
        <TeamSide team={visitor} name={awayTeamName} align="left" />

        <div className="v2-game-details-stats" style={{ width: "260px", flexShrink: 0 }}>
          {rows.map((row, i) => (
            <StatRow
              key={row.label}
              left={row.left}
              label={row.label}
              right={row.right}
              isLast={i === rows.length - 1}
            />
          ))}
        </div>

        <TeamSide team={home} name={homeTeamName} align="right" />
      </div>

      {(series || metaLine || stars.length > 0) && (
        <div
          className="flex flex-col items-center text-center"
          style={{ gap: "4px", borderTop: "1px solid #e2e8f0", paddingTop: "8px" }}
        >
          {series && (
            <div
              className="text-sm font-extrabold uppercase"
              style={{ color: "var(--v2-primary)", letterSpacing: "0.03em" }}
            >
              Season series: {series}
            </div>
          )}
          {metaLine && <div className="text-xs text-slate-500">{metaLine}</div>}
          {stars.length > 0 && (
            <div className="text-xs text-slate-500">
              {stars.map((p, i) => `${i + 1}. ${p.firstName} ${p.lastName}`).join(" · ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
